// Empty State Component
// =====================
// Friendly empty state with illustration, message, and CTA

import React from 'react';
import Button from './Button';

type IllustrationType = 'journeys' | 'topics' | 'sessions' | 'search' | 'generic';

interface EmptyStateProps {
  /** Type of illustration to show */
  type: IllustrationType;
  /** Main title text */
  title: string;
  /** Descriptive message */
  description: string;
  /** Primary action button text */
  actionText?: string;
  /** Primary action click handler */
  onAction?: () => void;
  /** Secondary action button text */
  secondaryActionText?: string;
  /** Secondary action click handler */
  onSecondaryAction?: () => void;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

// Illustration emojis and decorations for each type
const illustrations: Record<IllustrationType, { emoji: string; decorations: string[] }> = {
  journeys: {
    emoji: '🎯',
    decorations: ['📚', '✨', '🚀']
  },
  topics: {
    emoji: '📖',
    decorations: ['💡', '📝', '🔍']
  },
  sessions: {
    emoji: '🎙️',
    decorations: ['💬', '🤔', '✅']
  },
  search: {
    emoji: '🔍',
    decorations: ['❓', '📂', '🗂️']
  },
  generic: {
    emoji: '📭',
    decorations: ['✨', '🌟', '💫']
  }
};

const EmptyState: React.FC<EmptyStateProps> = ({
  type,
  title,
  description,
  actionText,
  onAction,
  secondaryActionText,
  onSecondaryAction,
  size = 'md'
}) => {
  const { emoji, decorations } = illustrations[type];

  const sizeClasses = {
    sm: {
      container: 'py-6 px-4',
      emoji: 'text-4xl',
      decoration: 'text-xl',
      title: 'text-base',
      description: 'text-xs',
      spacing: 'space-y-2'
    },
    md: {
      container: 'py-8 px-6',
      emoji: 'text-4xl',
      decoration: 'text-2xl',
      title: 'text-lg',
      description: 'text-sm',
      spacing: 'space-y-3'
    },
    lg: {
      container: 'py-12 px-8',
      emoji: 'text-8xl',
      decoration: 'text-3xl',
      title: 'text-xl',
      description: 'text-sm',
      spacing: 'space-y-4'
    }
  };

  const classes = sizeClasses[size];

  return (
    <div className={`flex flex-col items-center text-center ${classes.container}`}>
      {/* Illustration */}
      <div className="relative mb-4">
        {/* Decorative floating elements */}
        <div className="absolute -top-2 -left-4 animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}>
          <span className={`${classes.decoration} opacity-60`}>{decorations[0]}</span>
        </div>
        <div className="absolute -top-1 -right-4 animate-bounce" style={{ animationDelay: '0.5s', animationDuration: '2.5s' }}>
          <span className={`${classes.decoration} opacity-60`}>{decorations[1]}</span>
        </div>
        <div className="absolute -bottom-1 -right-2 animate-bounce" style={{ animationDelay: '1s', animationDuration: '2.8s' }}>
          <span className={`${classes.decoration} opacity-60`}>{decorations[2]}</span>
        </div>

        {/* Main emoji */}
        <div className={`${classes.emoji} mb-2 animate-pulse`} style={{ animationDuration: '3s' }}>
          {emoji}
        </div>
      </div>

      {/* Text content */}
      <div className={classes.spacing}>
        <h3 className={`${classes.title} font-semibold text-neutral-dark dark:text-white`}>
          {title}
        </h3>
        <p className={`${classes.description} text-neutral-medium max-w-xs mx-auto`}>
          {description}
        </p>
      </div>

      {/* Action buttons */}
      {(actionText || secondaryActionText) && (
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          {actionText && onAction && (
            <Button
              variant="primary"
              size={size === 'lg' ? 'md' : 'sm'}
              onClick={onAction}
            >
              {actionText}
            </Button>
          )}
          {secondaryActionText && onSecondaryAction && (
            <Button
              variant="outline"
              size={size === 'lg' ? 'md' : 'sm'}
              onClick={onSecondaryAction}
            >
              {secondaryActionText}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
